/**
 * The wind-chime simulation behind discordant.org.
 *
 * Eight tubes hang as rigid pendulums (cord + tube as one line) from anchors on the ring.
 * Each is integrated as a unit hang direction `d` and an angular velocity `w` (exact at
 * any lean, no coordinate singularities); the contract's tangent-slope `lean` is mirrored
 * from that every step. The clapper disc hangs from the ring centre, the sail hangs from
 * the clapper: a double pendulum integrated with position-based dynamics. Wind drag on the
 * sail drives the clapper into the tubes; contacts are impulses with a soft penetration
 * bias; hands brush, shove and pinch. Nothing here depends on audio.
 *
 * Coordinates: metres, Y up, Z toward the viewer.
 */
import type {
  ChimeWorld, ChimeWorldOptions, ClapperState, HandInput, PhysicsEvent, SailState, StrikeEvent, StrikeKind,
  TubeSpec, TubeState, Vec3,
} from '../types';
import { v2, v3 } from '../types';
import { CONFIG } from '../config';
import { BAR_PARTIALS, tubePointOf } from './geometry';
import { clamp01, mulberry32 } from '../util/math';
import { PHYS } from './params';
import { buildLayout } from './layout';
import { WindField } from './wind';
import { addScaled, closestOnSegment, closestSegSeg, copy, cross, dot, normalize, set, sub, type SegSegResult } from './collide';

const T = PHYS.tube, C = PHYS.contact, H = PHYS.hand, E = PHYS.energy, W = PHYS.wind;

interface TubeBody {
  /** unit hang direction from the anchor */
  d: Vec3;
  /** angular velocity, rad/s, kept perpendicular to d */
  w: Vec3;
  mass: number;
  /** moment of inertia about the pivot per unit mass: L^2/12 + rcm^2 */
  iUnit: number;
  /** pivot to centre of mass */
  rcm: number;
  /** gravity restoring rate: g*rcm/iUnit */
  omega2: number;
  /** drag reference: 0.5 * rho * Cd * 2 r L * dragScale / (mass) */
  dragK: number;
  top: Vec3;
  bottom: Vec3;
  mid: Vec3;
  /** angular acceleration accumulated this step */
  alpha: Vec3;
  /** energy decay time constant */
  tau: number;
  /** hold target this step from hands (0..1) */
  hold: number;
  pressedHold: boolean;
  lastDampAt: number;
}

/** an on-tube listener the debug overlay can attach without touching main.ts */
export type EventListener = (events: PhysicsEvent[], world: ChimeWorld) => void;

export function createChimeWorld(opts: ChimeWorldOptions): ChimeWorld {
  const { ring, tuningHz, quality } = opts;
  const layout = buildLayout(tuningHz, ring);
  const tubes: TubeSpec[] = layout.tubes;
  const n = tubes.length;
  const lite = quality.tier === 'lite';
  const rng = mulberry32(opts.seed);
  const rngDetune = mulberry32(opts.seed ^ 0x9e3779b9);
  const wind = new WindField(opts.seed);
  let gaussSpare: number | null = null;

  // ---- tubes --------------------------------------------------------------------------
  const tubeStates: TubeState[] = [];
  const bodies: TubeBody[] = [];
  for (let i = 0; i < n; i++) {
    const t = tubes[i];
    const rcm = t.cord + t.length / 2;
    const iUnit = (t.length * t.length) / 12 + rcm * rcm;
    const mass = layout.masses[i];
    const lx = (rng() - 0.5) * 0.006, lz = (rng() - 0.5) * 0.006;
    bodies.push({
      d: normalize(v3(), v3(lx, -1, lz)),
      w: v3(),
      mass, iUnit, rcm,
      omega2: (PHYS.g * rcm) / iUnit,
      dragK: (0.5 * PHYS.airDensity * T.dragCd * 2 * t.radius * t.length * T.dragScale) / mass,
      top: v3(), bottom: v3(), mid: v3(), alpha: v3(),
      tau: E.tauBase + E.tauSpread * (1 - t.freqHz / layout.fMax),
      hold: 0, pressedHold: false, lastDampAt: -1e9,
    });
    tubeStates.push({ lean: v2(lx, lz), leanVel: v2(), energy: 0, damped: 0, detuneCents: 0 });
  }
  // initial detune: a draw from the stationary distribution
  const detuneBound = CONFIG.audio.detuneCents;
  for (let i = 0; i < n; i++) tubeStates[i].detuneCents = clampAbs(gauss() * PHYS.detune.stdCents, detuneBound);

  // mutual aid: coupling by shared partials (same rule as the audio lane)
  const aid = new Float64Array(n * n);
  for (let i = 0; i < n; i++) for (let j = 0; j < n; j++) {
    if (i === j) continue;
    let near = false;
    for (const pi of BAR_PARTIALS) {
      for (const pj of BAR_PARTIALS) {
        const a = tubes[i].freqHz * pi, b = tubes[j].freqHz * pj;
        if (Math.abs(a / b - 1) < E.aidTolerance) { near = true; break; }
      }
      if (near) break;
    }
    aid[i * n + j] = near ? E.aidNear : E.aidFar;
  }

  // ---- clapper and sail (PBD particles) ---------------------------------------------
  const clapperCord = layout.clapperCord;
  const pivot = ring.center;
  const pc = v3(pivot.x + 0.003, pivot.y - clapperCord, pivot.z - 0.002);
  const vc = v3();
  const ps = v3(pc.x + 0.004, pc.y - PHYS.sail.cord, pc.z + 0.003);
  const vs = v3();
  const mc = PHYS.clapper.mass, ms = PHYS.sail.mass;
  const sailArea = (2 * PHYS.sail.halfSize) * (2 * PHYS.sail.halfSize);
  /**
   * Aerodynamic gain from the geometry: at W.reachSpeed the assembly's static lean must
   * reach the median tube. Static: F = lean * (mc + ms) * g; the sail's own lean under that
   * force is F / (ms g), which tilts it and shrinks its face to the wind.
   */
  const windGain = (() => {
    const dists = tubes.map((t) => Math.hypot(t.anchor.x - ring.center.x, t.anchor.z - ring.center.z)).sort((a, b) => a - b);
    const dMed = 0.5 * (dists[(n - 1) >> 1] + dists[n >> 1]);
    const rBar = tubes.reduce((a, t) => a + t.radius, 0) / n;
    const lean = Math.max(0.05, (dMed - PHYS.clapper.radius - rBar) / layout.clapperCord);
    const F = lean * (mc + ms) * PHYS.g;
    const sailLean = F / (ms * PHYS.g);
    const tilt = Math.max(0.15, 1 / Math.sqrt(1 + sailLean * sailLean));
    const drag = 0.5 * PHYS.airDensity * (PHYS.sail.dragCd * sailArea * tilt + PHYS.clapper.dragCd * PHYS.clapper.dragArea) * W.reachSpeed * W.reachSpeed;
    return (W.gainTrim * F) / drag;
  })();
  const sailDragK = (0.5 * PHYS.airDensity * PHYS.sail.dragCd * sailArea * windGain) / ms;
  const clapperDragK = (0.5 * PHYS.airDensity * PHYS.clapper.dragCd * PHYS.clapper.dragArea * windGain) / mc;
  wind.gain = windGain;
  let lastStrikeAt = -1e9;

  const clapper: ClapperState = { lean: v2(), leanVel: v2(), pos: v3(), radius: PHYS.clapper.radius };
  const sail: SailState = { lean: v2(), leanVel: v2(), pos: v3(), halfSize: PHYS.sail.halfSize };

  // ---- hands --------------------------------------------------------------------------
  const hands: HandInput[] = Array.from({ length: CONFIG.hand.max }, () => ({
    id: -1, active: false, pos: v3(0, 0, CONFIG.hand.planeZ), vel: v3(), screen: v2(),
    radius: CONFIG.hand.radius, pressed: false, still: 0,
  }));
  const hTouch = new Uint8Array(hands.length * n);
  const hMoving = new Uint8Array(hands.length);
  const hLastStrike = new Float64Array(hands.length * n).fill(-1e9);

  // ---- contact bookkeeping --------------------------------------------------------------
  // keys: clapper-tube i -> i; sail-tube i -> n+i; tube i-j (i<j) -> 2n + i*n + j
  const nKeys = 2 * n + n * n;
  const touching = new Uint8Array(nKeys);
  const lastEmit = new Float64Array(nKeys).fill(-1e9);
  const pairSkip = new Uint8Array(n * n);
  for (let i = 0; i < n; i++) for (let j = i + 1; j < n; j++) {
    const a = tubes[i].anchor, b = tubes[j].anchor;
    const da = Math.hypot(a.x - b.x, a.z - b.z);
    const reach = (tubes[i].cord + tubes[i].length + tubes[j].cord + tubes[j].length) * 0.5 + tubes[i].radius + tubes[j].radius;
    if (da > reach || (lite && da > C.liteAnchorReach)) pairSkip[i * n + j] = 1;
  }

  // ---- scratch --------------------------------------------------------------------------
  const tmpA = v3(), tmpB = v3(), tmpN = v3(), tmpV = v3(), tmpW = v3(), tmpQ = v3(), tmpF = v3();
  const seg: SegSegResult = { s: 0, t: 0 };
  const gVec = v3(0, -PHYS.g, 0);
  let events: PhysicsEvent[] = [];
  const listeners: EventListener[] = [];

  function gauss(): number {
    if (gaussSpare !== null) { const g = gaussSpare; gaussSpare = null; return g; }
    let u = 0, v = 0;
    while (u <= 1e-12) u = rngDetune();
    v = rngDetune();
    const r = Math.sqrt(-2 * Math.log(u));
    gaussSpare = r * Math.sin(2 * Math.PI * v);
    return r * Math.cos(2 * Math.PI * v);
  }

  // ---- helpers ------------------------------------------------------------------------
  /** velocity of the point at distance r along tube i */
  function pointVel(b: TubeBody, r: number, out: Vec3): Vec3 {
    cross(out, b.w, b.d);
    out.x *= r; out.y *= r; out.z *= r;
    return out;
  }

  function strike(i: number, velocity: number, kind: StrikeKind, pos: Vec3, s: number, other?: number) {
    const ev: StrikeEvent = { type: 'strike', tube: i, velocity, kind, pos: copy(v3(), pos), s };
    if (other !== undefined) ev.other = other;
    events.push(ev);
    lastStrikeAt = world.time;
    const k = kind === 'clapper' ? E.kClapper : kind === 'tube' ? E.kTube : E.kHand;
    const st = tubeStates[i];
    st.energy = Math.min(1, st.energy + velocity * k);
    for (let j = 0; j < n; j++) {
      if (j === i) continue;
      const sj = tubeStates[j];
      sj.energy = Math.min(1, sj.energy + velocity * aid[i * n + j]);
    }
  }

  /**
   * Sphere (mass m, centre p, velocity v, radius R) against tube i. Applies impulses to
   * both, emits a strike on the rising edge of contact. Returns true while touching.
   */
  function sphereVsTube(key: number, i: number, p: Vec3, v: Vec3, R: number, invMass: number, dt: number, kind: StrikeKind, velScale: number, minSpeed: number, emit: boolean): boolean {
    const b = bodies[i], t = tubes[i];
    const s = closestOnSegment(b.top, b.bottom, p);
    const r = t.cord + s * t.length;
    addScaled(tmpQ, t.anchor, b.d, r);
    sub(tmpN, p, tmpQ);
    const dist = Math.sqrt(dot(tmpN, tmpN));
    const depth = R + t.radius - dist;
    const was = touching[key] === 1;
    const now = depth > (was ? -C.releaseMargin : 0);
    touching[key] = now ? 1 : 0;
    if (!now) return false;
    if (dist < 1e-6) return true;
    tmpN.x /= dist; tmpN.y /= dist; tmpN.z /= dist;
    pointVel(b, r, tmpV);
    const vn = (v.x - tmpV.x) * tmpN.x + (v.y - tmpV.y) * tmpN.y + (v.z - tmpV.z) * tmpN.z;
    cross(tmpW, b.d, tmpN);
    const dn2 = dot(tmpW, tmpW);
    const wt = (r * r * dn2) / (b.mass * b.iUnit);
    const wsum = wt + invMass;
    if (emit && !was && vn < -minSpeed && world.time - lastEmit[key] > C.cooldown) {
      lastEmit[key] = world.time;
      strike(i, clamp01((-vn * velScale) / C.velocityRef), kind, tmpQ, s);
    }
    let P = 0;
    if (vn < 0) P += (-(1 + (vn < -0.1 ? C.restitution : 0)) * vn) / wsum;
    P += (C.bias * Math.max(0, depth - C.slop)) / dt / wsum;
    if (P > 0) {
      v.x += tmpN.x * P * invMass; v.y += tmpN.y * P * invMass; v.z += tmpN.z * P * invMass;
      const k = (r * P) / (b.mass * b.iUnit);
      b.w.x -= tmpW.x * k; b.w.y -= tmpW.y * k; b.w.z -= tmpW.z * k;
    }
    return true;
  }

  function tubeVsTube(i: number, j: number, dt: number) {
    const bi = bodies[i], bj = bodies[j], ti = tubes[i], tj = tubes[j];
    // cheap reject: mid points further apart than the reach
    const dx = bi.mid.x - bj.mid.x, dy = bi.mid.y - bj.mid.y, dz = bi.mid.z - bj.mid.z;
    const reach = (ti.length + tj.length) * 0.5 + ti.radius + tj.radius + 0.02;
    if (dx * dx + dy * dy + dz * dz > reach * reach) { touching[2 * n + i * n + j] = 0; return; }
    closestSegSeg(bi.top, bi.bottom, bj.top, bj.bottom, seg);
    const ri = ti.cord + seg.s * ti.length, rj = tj.cord + seg.t * tj.length;
    addScaled(tmpA, ti.anchor, bi.d, ri);
    addScaled(tmpB, tj.anchor, bj.d, rj);
    sub(tmpN, tmpB, tmpA);
    const dist = Math.sqrt(dot(tmpN, tmpN));
    const depth = ti.radius + tj.radius - dist;
    const key = 2 * n + i * n + j;
    const was = touching[key] === 1;
    const now = depth > (was ? -C.releaseMargin : 0);
    touching[key] = now ? 1 : 0;
    if (!now || dist < 1e-6) return;
    tmpN.x /= dist; tmpN.y /= dist; tmpN.z /= dist;
    pointVel(bi, ri, tmpV);
    pointVel(bj, rj, tmpW);
    const vn = (tmpW.x - tmpV.x) * tmpN.x + (tmpW.y - tmpV.y) * tmpN.y + (tmpW.z - tmpV.z) * tmpN.z;
    cross(tmpV, bi.d, tmpN);
    cross(tmpW, bj.d, tmpN);
    const wi = (ri * ri * dot(tmpV, tmpV)) / (bi.mass * bi.iUnit);
    const wj = (rj * rj * dot(tmpW, tmpW)) / (bj.mass * bj.iUnit);
    const wsum = wi + wj;
    if (wsum < 1e-9) return;
    if (!was && vn < -C.minStrikeSpeed && world.time - lastEmit[key] > C.cooldown) {
      lastEmit[key] = world.time;
      const vel = clamp01(-vn / C.velocityRef);
      strike(i, vel, 'tube', tmpA, seg.s, j);
      strike(j, vel, 'tube', tmpB, seg.t, i);
    }
    let P = 0;
    if (vn < 0) P += (-(1 + (vn < -0.1 ? C.restitution : 0)) * vn) / wsum;
    P += (C.bias * Math.max(0, depth - C.slop)) / dt / wsum;
    if (P > 0) {
      const ki = (ri * P) / (bi.mass * bi.iUnit), kj = (rj * P) / (bj.mass * bj.iUnit);
      bi.w.x -= tmpV.x * ki; bi.w.y -= tmpV.y * ki; bi.w.z -= tmpV.z * ki;
      bj.w.x += tmpW.x * kj; bj.w.y += tmpW.y * kj; bj.w.z += tmpW.z * kj;
    }
  }

  function handsStep(dt: number) {
    for (let i = 0; i < n; i++) { bodies[i].hold = 0; bodies[i].pressedHold = false; }
    for (let h = 0; h < hands.length; h++) {
      const hand = hands[h];
      if (!hand.active) {
        hMoving[h] = 0;
        hTouch.fill(0, h * n, h * n + n);
        continue;
      }
      const vx = clampAbs(hand.vel.x, H.maxSpeed), vy = clampAbs(hand.vel.y, H.maxSpeed);
      const speed = Math.hypot(vx, vy);
      // the physical shove saturates below the event velocity: a swipe brushes, it does not fling
      const shove = Math.min(1, H.shoveMaxSpeed / Math.max(speed, 1e-6));
      const sx = vx * shove, sSpeed = speed * shove;
      const wasMoving = hMoving[h] === 1;
      const moving = wasMoving ? speed > H.stopSpeed : speed > H.moveSpeed;
      hMoving[h] = moving ? 1 : 0;
      const still = !moving && hand.still > H.stillTime;
      const hx = hand.pos.x, hy = hand.pos.y;
      for (let i = 0; i < n; i++) {
        const b = bodies[i], t = tubes[i];
        // XY-only proximity: the hand reaches through the whole depth
        const ax = b.top.x, ay = b.top.y, bx = b.bottom.x - ax, by = b.bottom.y - ay;
        const l2 = bx * bx + by * by;
        let s = l2 > 1e-9 ? ((hx - ax) * bx + (hy - ay) * by) / l2 : 0;
        s = s < 0 ? 0 : s > 1 ? 1 : s;
        const px = ax + bx * s, py = ay + by * s;
        const dist = Math.hypot(hx - px, hy - py);
        const key = h * n + i;
        const was = hTouch[key] === 1;
        const overlap = dist < hand.radius + t.radius + (was ? C.releaseMargin : 0);
        hTouch[key] = overlap ? 1 : 0;
        if (!overlap) continue;
        const r = t.cord + s * t.length;
        if (moving) {
          // shove: pull the contact point toward the hand velocity while the hand moves into
          // the tube (not once it is past), and part the curtain in Z
          const zPart = H.zPart * sSpeed * (b.mid.z >= CONFIG.hand.planeZ ? 1 : -1);
          const into = vx * (px - hx) + vy * (py - hy) > 0;
          if (into) {
            pointVel(b, r, tmpV);
            set(tmpF, H.followRate * (H.follow * sx - tmpV.x), 0, H.followRate * (zPart - tmpV.z));
            cross(tmpW, b.d, tmpF);
            addScaled(b.alpha, b.alpha, tmpW, 1 / r);
          }
          if ((!was || !wasMoving) && world.time - hLastStrike[key] > H.cooldown) {
            hLastStrike[key] = world.time;
            set(tmpF, H.entryGain * sx, 0, H.entryGain * zPart);
            cross(tmpW, b.d, tmpF);
            addScaled(b.w, b.w, tmpW, 1 / r);
            addScaled(tmpQ, t.anchor, b.d, r);
            strike(i, clamp01((speed * H.velocityScale) / C.velocityRef), 'hand', tmpQ, s);
          }
        } else {
          const target = still ? 1 : H.slowHold;
          if (target > b.hold) b.hold = target;
          if (hand.pressed) b.pressedHold = true;
          // pinch: pull the contact point (in X) into the hand so the tube stays held
          set(tmpF, H.holdSpring * target * (hx - px), 0, 0);
          cross(tmpW, b.d, tmpF);
          addScaled(b.alpha, b.alpha, tmpW, 1 / r);
        }
      }
    }
  }

  function integrateTubes(dt: number) {
    for (let i = 0; i < n; i++) {
      const b = bodies[i];
      addScaled(b.w, b.w, b.alpha, dt);
      // d' = w x d
      cross(tmpV, b.w, b.d);
      addScaled(b.d, b.d, tmpV, dt);
      normalize(b.d, b.d);
      // a line has no spin about itself
      const along = dot(b.w, b.d);
      b.w.x -= b.d.x * along; b.w.y -= b.d.y * along; b.w.z -= b.d.z * along;
      // safety net: never past maxLean, and never above the horizontal
      const h = -b.d.y;
      const horiz = Math.hypot(b.d.x, b.d.z);
      if (h < 1e-3 || horiz / h > T.maxLean) {
        const hh = Math.max(h, 1e-3);
        const k = (T.maxLean * hh) / Math.max(horiz, 1e-9);
        b.d.x *= k; b.d.z *= k; b.d.y = -hh;
        normalize(b.d, b.d);
        b.w.x *= 0.5; b.w.y *= 0.5; b.w.z *= 0.5;
      }
    }
  }

  function integrateClapper(dt: number) {
    // forces on the particles (accelerations)
    wind.sample(pc, tmpV);
    const rc = PHYS.clapper.relVel;
    tmpV.x -= vc.x * rc; tmpV.y = (tmpV.y - vc.y * rc) * 0.3; tmpV.z -= vc.z * rc;
    let m = Math.sqrt(dot(tmpV, tmpV));
    vc.x += (clapperDragK * m * tmpV.x - PHYS.clapper.damping * vc.x + gVec.x) * dt;
    vc.y += (clapperDragK * m * tmpV.y - PHYS.clapper.damping * vc.y + gVec.y) * dt;
    vc.z += (clapperDragK * m * tmpV.z - PHYS.clapper.damping * vc.z + gVec.z) * dt;
    // soft fence at the ring line (in ellipse-normalised coordinates)
    const ex = (pc.x - pivot.x) / ring.rx, ez = (pc.z - pivot.z) / ring.rz;
    const re = Math.hypot(ex, ez);
    if (re > PHYS.clapper.fence) {
      const k = (PHYS.clapper.fenceStiffness * (re - PHYS.clapper.fence)) / re;
      vc.x -= k * ex * ring.rx * dt;
      vc.z -= k * ez * ring.rz * dt;
    }
    wind.sample(ps, tmpV);
    const rs = PHYS.sail.relVel;
    tmpV.x -= vs.x * rs; tmpV.y = (tmpV.y - vs.y * rs) * 0.25; tmpV.z -= vs.z * rs;
    m = Math.sqrt(dot(tmpV, tmpV));
    // a sail that flies up toward horizontal presents less face to a horizontal wind
    const tilt = Math.max(0.15, (pc.y - ps.y) / PHYS.sail.cord);
    const ks = sailDragK * m * tilt;
    vs.x += (ks * tmpV.x - PHYS.sail.damping * vs.x + gVec.x) * dt;
    vs.y += (ks * tmpV.y - PHYS.sail.damping * vs.y + gVec.y) * dt;
    vs.z += (ks * tmpV.z - PHYS.sail.damping * vs.z + gVec.z) * dt;
  }

  function projectClapper(dt: number) {
    // predict
    addScaled(tmpA, pc, vc, dt);
    addScaled(tmpB, ps, vs, dt);
    const wc = 1 / mc, ws = 1 / ms, wsum = wc + ws;
    for (let it = 0; it < 3; it++) {
      // sail cord
      sub(tmpN, tmpB, tmpA);
      let l = Math.sqrt(dot(tmpN, tmpN));
      if (l > 1e-9) {
        const corr = (l - PHYS.sail.cord) / l;
        addScaled(tmpA, tmpA, tmpN, (corr * wc) / wsum);
        addScaled(tmpB, tmpB, tmpN, (-corr * ws) / wsum);
      }
      // the sail never flies above the clapper: keep a minimum drop
      const minDrop = PHYS.sail.minDrop * PHYS.sail.cord;
      if (tmpB.y > tmpA.y - minDrop) {
        const dy = tmpB.y - (tmpA.y - minDrop);
        tmpB.y -= (dy * ws) / wsum;
        tmpA.y += (dy * wc) / wsum;
      }
      // clapper cord (pivot is fixed)
      sub(tmpN, tmpA, pivot);
      l = Math.sqrt(dot(tmpN, tmpN));
      if (l > 1e-9) addScaled(tmpA, pivot, tmpN, clapperCord / l);
    }
    sub(vc, tmpA, pc); vc.x /= dt; vc.y /= dt; vc.z /= dt;
    sub(vs, tmpB, ps); vs.x /= dt; vs.y /= dt; vs.z /= dt;
    copy(pc, tmpA);
    copy(ps, tmpB);
  }

  function mirror() {
    for (let i = 0; i < n; i++) {
      const b = bodies[i], st = tubeStates[i];
      const h = -b.d.y;
      st.lean.x = b.d.x / h;
      st.lean.y = b.d.z / h;
      cross(tmpV, b.w, b.d);
      const hh = h * h;
      st.leanVel.x = (-tmpV.x * b.d.y + b.d.x * tmpV.y) / hh;
      st.leanVel.y = (-tmpV.z * b.d.y + b.d.z * tmpV.y) / hh;
    }
    leanOf(pc, vc, pivot, null, clapper);
    leanOf(ps, vs, pc, vc, sail);
    copy(clapper.pos, pc);
    copy(sail.pos, ps);
    clapper.radius = PHYS.clapper.radius;
    sail.halfSize = PHYS.sail.halfSize;
  }

  function leanOf(p: Vec3, v: Vec3, piv: Vec3, pivVel: Vec3 | null, out: { lean: { x: number; y: number }; leanVel: { x: number; y: number } }) {
    const dx = p.x - piv.x, dz = p.z - piv.z, h = Math.max(1e-3, piv.y - p.y);
    const vx = v.x - (pivVel ? pivVel.x : 0), vy = v.y - (pivVel ? pivVel.y : 0), vz = v.z - (pivVel ? pivVel.z : 0);
    out.lean.x = dx / h;
    out.lean.y = dz / h;
    const hh = h * h;
    out.leanVel.x = (vx * h + dx * vy) / hh;
    out.leanVel.y = (vz * h + dz * vy) / hh;
  }

  // ---- the step -------------------------------------------------------------------------
  const world: ChimeWorld = {
    tubes, tubeStates, clapper, sail, ring, hands,
    time: 0,
    get gust() { return wind.gust; },
    step(dt: number): PhysicsEvent[] {
      events = [];
      world.time += dt;
      const t = world.time;
      wind.silence = t - Math.max(0, lastStrikeAt);
      wind.update(t, dt);

      // 1. geometry and forces on tubes
      for (let i = 0; i < n; i++) {
        const b = bodies[i], spec = tubes[i];
        addScaled(b.top, spec.anchor, b.d, spec.cord);
        addScaled(b.bottom, spec.anchor, b.d, spec.cord + spec.length);
        addScaled(b.mid, spec.anchor, b.d, b.rcm);
        // gravity: alpha = (rcm/I) d x g
        cross(b.alpha, b.d, gVec);
        const kg = b.rcm / b.iUnit;
        b.alpha.x *= kg; b.alpha.y *= kg; b.alpha.z *= kg;
        // linear damping (structural + still air), stronger while a hand holds
        const st = tubeStates[i];
        const damp = T.damping + (b.pressedHold ? H.pressedHoldDamping : H.holdDamping) * st.damped;
        addScaled(b.alpha, b.alpha, b.w, -damp);
        // wind drag at the centre of mass, perpendicular part only
        wind.sample(b.mid, tmpV);
        pointVel(b, b.rcm, tmpW);
        sub(tmpV, tmpV, tmpW);
        const along = dot(tmpV, b.d);
        addScaled(tmpV, tmpV, b.d, -along);
        const m = Math.sqrt(dot(tmpV, tmpV));
        // F/m = dragK * |v| v ; alpha += rcm (d x F/m) / I
        cross(tmpW, b.d, tmpV);
        addScaled(b.alpha, b.alpha, tmpW, (b.dragK * m * b.rcm) / b.iUnit);
      }

      // 2. hands (need tube geometry; add to alpha / w, emit hand strikes, set hold)
      handsStep(dt);

      // 3. clapper and sail forces
      integrateClapper(dt);

      // 4. contacts
      for (let i = 0; i < n; i++) {
        sphereVsTube(i, i, pc, vc, PHYS.clapper.radius, 1 / mc, dt, 'clapper', 1, C.minStrikeSpeed, true);
        sphereVsTube(n + i, i, ps, vs, PHYS.sail.collisionRadius, 1 / ms, dt, 'clapper', PHYS.sail.strikeVelocityScale, PHYS.sail.strikeMinSpeed, PHYS.sail.strikes);
      }
      for (let i = 0; i < n; i++) for (let j = i + 1; j < n; j++) {
        if (pairSkip[i * n + j]) continue;
        tubeVsTube(i, j, dt);
      }

      // 5. integrate
      integrateTubes(dt);
      projectClapper(dt);

      // 6. energy, damping, detune
      const sigma = PHYS.detune.stdCents * Math.sqrt(2 / PHYS.detune.tau) * Math.sqrt(dt);
      for (let i = 0; i < n; i++) {
        const b = bodies[i], st = tubeStates[i];
        const target = Math.min(1, b.hold);
        const rate = target > st.damped ? H.dampRise * (b.pressedHold ? 1.5 : 1) : H.dampFall;
        st.damped += (target - st.damped) * (1 - Math.exp(-rate * dt));
        if (st.damped < 1e-4) st.damped = 0;
        const decay = (1 - st.damped) / b.tau + st.damped / E.tauDamped;
        st.energy *= Math.exp(-decay * dt);
        if (st.energy < 1e-5) st.energy = 0;
        if (b.hold > 0 && st.damped > 0.05 && t - b.lastDampAt > H.dampEventInterval) {
          b.lastDampAt = t;
          events.push({ type: 'damp', tube: i, amount: st.damped });
        }
        st.detuneCents = clampAbs(st.detuneCents - (st.detuneCents / PHYS.detune.tau) * dt + sigma * gauss(), detuneBound);
      }

      // 7. mirror to the contract
      mirror();
      if (listeners.length) for (const l of listeners) l(events, world);
      return events;
    },
    tubePoint(i: number, s: number, out?: Vec3): Vec3 {
      return tubePointOf(tubes[i], tubeStates[i], s, out);
    },
    windAt(pos: Vec3, out: Vec3 = v3()): Vec3 {
      return wind.sample(pos, out);
    },
  };

  mirror();

  // optional overlay, in its own module so the sim stays DOM-free
  if (typeof location !== 'undefined' && typeof document !== 'undefined' && location.search.includes('debug=physics')) {
    void import('./debugOverlay').then((m) => m.mountPhysicsDebug(world, wind, (l) => listeners.push(l)));
  }

  return world;
}

function clampAbs(v: number, lim: number): number {
  return v < -lim ? -lim : v > lim ? lim : v;
}
